// ===========================================
// PROVIDER TYPES (API Services)
// ===========================================

/** Image generation API providers. 'profile' = a published generator profile (image-only). */
export type ImageProvider = 'openrouter' | 'runware' | 'vertexai' | 'profile';

/** Video generation API providers */
export type VideoProvider = 'runware' | 'vertexai';

/** Audio generation API providers */
export type AudioProvider = 'suno' | 'runware';

/** Chat/Text inference API providers */
export type ChatProvider = 'openrouter' | 'vertexai';

/** Chat providers beyond ChatProvider that a pipeline checkpoint can run on
 *  (prompt, distributor, sequencer and agent steps): Anthropic's own API, on a
 *  server with its key. The Builder chat and the reviewer tiers do not dispatch it. */
export type ExtraChatProvider = 'anthropic';

/** All provider types */
export type Provider = ImageProvider | VideoProvider | AudioProvider | ChatProvider | ExtraChatProvider;

/** All generator types (for media generation) */
export type Generator = ImageProvider | VideoProvider | AudioProvider;

// ===========================================
// PROVIDER DEFINITIONS
// ===========================================

export interface ProviderDefinition {
  value: string;
  label: string;
  icon?: string;
}

export const IMAGE_PROVIDERS: ProviderDefinition[] = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'runware', label: 'Runware' },
  { value: 'vertexai', label: 'Vertex AI' },
  { value: 'profile', label: 'Profile' },
];

export const VIDEO_PROVIDERS: ProviderDefinition[] = [
  { value: 'runware', label: 'Runware' },
  { value: 'vertexai', label: 'Vertex AI' },
];

export const AUDIO_PROVIDERS: ProviderDefinition[] = [
  { value: 'suno', label: 'Suno' },
  { value: 'runware', label: 'Runware' },
];

export const CHAT_PROVIDERS: ProviderDefinition[] = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'vertexai', label: 'Vertex AI' },
];

/** The extra chat providers, offered beside CHAT_PROVIDERS where a checkpoint's
 *  model is chosen (the pipeline editor's Prompt, Distributor, Sequencer and
 *  Agent settings) once the chat catalog lists a model of theirs, which it does
 *  only on a server that has the provider's key. */
export const EXTRA_CHAT_PROVIDERS: ProviderDefinition[] = [{ value: 'anthropic', label: 'Anthropic' }];

/**
 * For panes whose backend handler still cannot dispatch anything but
 * OpenRouter. The AI Editor is the one remaining case: its handler
 * (`internal/api/clipeditor/handler.go`) hardcodes `ProviderOpenRouter`
 * directly rather than going through `pkg/aiChat`, so offering Vertex AI
 * there would fail.
 *
 * The Prompt Enhancer pane used to need this too — it funnels into
 * `pkg/aiChat.GenerateWithTelemetry`, which gained Vertex AI dispatch in
 * commit f211a52, so that call site was lifted back to CHAT_PROVIDERS on
 * 2026-08-07.
 *
 * Pass this to ChatProviderSelector rather than letting a pane offer an
 * option its handler will reject, and lift a call site back to
 * CHAT_PROVIDERS once its handler gains vertex dispatch too.
 */
export const OPENROUTER_ONLY_CHAT_PROVIDERS: ProviderDefinition[] = CHAT_PROVIDERS.filter(
  (p) => p.value === 'openrouter'
);

// ===========================================
// PROVIDER -> RESPONSE KEY MAPPING
// ===========================================

/** Maps provider to the key in ModelsResponse */
export type ProviderResponseKey = 'openrouter' | 'runware' | 'vertexai' | 'suno' | 'anthropic';

export const PROVIDER_RESPONSE_KEYS: Record<string, ProviderResponseKey | null> = {
  openrouter: 'openrouter',
  runware: 'runware',
  vertexai: 'vertexai',
  suno: 'suno',
  anthropic: 'anthropic',
};

/** Providers that require model selection */
export const PROVIDERS_WITH_MODELS: Provider[] = ['openrouter', 'runware', 'vertexai', 'suno', 'profile', 'anthropic'];

/** Check if a provider requires model selection */
export function providerRequiresModel(provider: Provider): boolean {
  return PROVIDERS_WITH_MODELS.includes(provider);
}

/** Get the response key for a provider */
export function getProviderResponseKey(provider: Provider): ProviderResponseKey | null {
  return PROVIDER_RESPONSE_KEYS[provider] ?? null;
}

// ===========================================
// MODEL TYPES (Actual AI Models)
// ===========================================

/** Image model ID string (e.g., 'openai/gpt-5-image-mini') */
export type ImageModel = string;

/** Video model ID string (e.g., 'lightricks:2@1') */
export type VideoModel = string;

/** Audio model ID string */
export type AudioModel = string;

/** Chat model ID string (e.g., 'anthropic/claude-3-opus') */
export type ChatModel = string;

// ===========================================
// SELECTION STATE TYPES
// ===========================================

export interface ImageSelection {
  provider: ImageProvider;
  model: ImageModel;
}

export interface VideoSelection {
  provider: VideoProvider;
  model: VideoModel;
}

export interface AudioSelection {
  provider: AudioProvider;
  model: AudioModel;
}

export interface ChatSelection {
  provider: ChatProvider;
  model: ChatModel;
}

// ===========================================
// DEFAULT VALUES
// ===========================================

export const DEFAULT_IMAGE_PROVIDER: ImageProvider = 'openrouter';
export const DEFAULT_IMAGE_MODEL: ImageModel = 'openai/gpt-5-image-mini';

export const DEFAULT_VIDEO_PROVIDER: VideoProvider = 'runware';
export const DEFAULT_VIDEO_MODEL: VideoModel = 'lightricks:2@1';

export const DEFAULT_AUDIO_PROVIDER: AudioProvider = 'suno';
export const DEFAULT_AUDIO_MODEL: AudioModel = 'V4_5ALL';

export const DEFAULT_CHAT_PROVIDER: ChatProvider = 'openrouter';
export const DEFAULT_CHAT_MODEL: ChatModel = 'x-ai/grok-4';
